// src/pages/Media.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Select,
  MenuItem,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  styled,
  Chip
} from '@mui/material';

import { CloudUpload, InsertDriveFile, Image } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import Cookies from "js-cookie";

const ModernPaper = styled('div')(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0px 4px 24px rgba(0,0,0,0.08)',
  backgroundColor: '#fff',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(6),
  transition: 'transform 0.2s ease-in-out',
  '&:hover': { transform: 'translateY(-2px)' }
}));

const GradientButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#581745',
  color: 'white',
  fontWeight: 600,
  padding: '12px 24px',
  borderRadius: '12px',
  textTransform: 'none',
  '&:hover': { backgroundColor: '#1f93d1' }
}));

export default function MediaManager() {

  // Redirect if no token
  useEffect(() => {
    const t = Cookies.get("authToken");
    if (!t) window.location.href = "/";
  }, []);

  const token = Cookies.get("authToken");

  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  // UI States
  const [selectedType, setSelectedType] = useState('Image');
  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const [uploadResult, setUploadResult] = useState({ success: false, message: '' });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [mediaList, setMediaList] = useState([]);

  // ----------------------------
  // Fetch Media List
  // ----------------------------
  const fetchMediaList = useCallback(async () => {
    try {
      const resp = await fetch('/api/api/media/', {
        headers: getAuthHeaders()
      });

      if (!resp.ok) throw new Error(`Error ${resp.status}`);

      const data = await resp.json();
      setMediaList(data);

    } catch (err) {
      console.error('Failed to fetch media list:', err);
    }
  }, [getAuthHeaders]);

  // initial data load
  useEffect(() => {
    fetchMediaList();
  }, [fetchMediaList]);

  // ----------------------------
  // Handle Upload
  // ----------------------------
  const handleFileChange = e => {
    setSelectedFile(e.target.files[0] || null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !fileName) {
      setUploadResult({ success: false, message: 'Please pick a file and enter a name.' });
      setUploadModalOpen(true);
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('fileName', fileName);
    formData.append('type', selectedType);

    try {
      const resp = await fetch('/api/api/media/upload', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      const result = await resp.json();
      if (!resp.ok) throw new Error(result.detail || 'Upload failed');

      setUploadResult({
        success: true,
        message: `Saved as ${result.filename}`
      });

      await fetchMediaList();

    } catch (err) {
      console.error(err);
      setUploadResult({ success: false, message: err.message });

    } finally {
      setUploadModalOpen(true);
      setSelectedFile(null);
    }
  };

  // ----------------------------
  // Delete Handler
  // ----------------------------
  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const resp = await fetch(`/api/api/media/${deleteTarget.uniqueId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!resp.ok) throw new Error(`Error ${resp.status}`);

      await fetchMediaList();

    } catch (err) {
      console.error('Delete failed:', err);

    } finally {
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    }
  };

  // ----------------------------
  // Table Columns
  // ----------------------------
  const columns = [
    {
      accessorKey: 'type',
      header: 'Type',
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          sx={{
            bgcolor: cell.getValue() === 'Image' ? '#eef2ff' : '#f5f3ff',
            color: cell.getValue() === 'Image' ? '#4338ca' : '#6d28d9',
            borderRadius: '6px'
          }}
        />
      )
    },
    { accessorKey: 'name', header: 'Name', muiTableBodyCellProps: { sx: { fontWeight: 500 } } },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => (
        <Chip label={cell.getValue()} color="success" variant="outlined" size="small" />
      )
    },
    {
      accessorKey: 'uniqueId',
      header: 'Filename',
      muiTableBodyCellProps: { sx: { color: '#6b7280' } }
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      Cell: ({ row }) => (
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={() => {
            setDeleteTarget(row.original);
            setDeleteModalOpen(true);
          }}
          sx={{ textTransform: 'none' }}
        >
          Delete
        </Button>
      )
    }
  ];

  // ----------------------------
  // Render
  // ----------------------------
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
      <h2 className="text-2xl font-bold text-gray-900">Media Manager</h2>

      {/* Upload Panel */}
      <ModernPaper>
        <Grid container spacing={4} alignItems="center">

          {/* Type */}
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
              sx={{
                borderRadius: '12px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' }
              }}
            >
              <MenuItem value="Image">
                <Image fontSize="small" /> Image
              </MenuItem>
              <MenuItem value="PDF">
                <InsertDriveFile fontSize="small" /> PDF
              </MenuItem>
            </Select>
          </Grid>

          {/* File Name */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="File Name"
              value={fileName}
              onChange={e => setFileName(e.target.value)}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>

          {/* File Select */}
          <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <input
              type="file"
              accept={selectedType === 'Image' ? 'image/*' : 'application/pdf'}
              hidden
              id="media-upload"
              onChange={handleFileChange}
            />

            <label htmlFor="media-upload">
              <GradientButton component="span" startIcon={<CloudUpload />}>
                Choose File
              </GradientButton>
            </label>

            {selectedFile && <Typography>{selectedFile.name}</Typography>}
          </Grid>

          {/* Upload button */}
          <Grid item xs={12} md={2}>
            <GradientButton
              fullWidth
              onClick={handleUpload}
              disabled={!selectedFile || !fileName}
              endIcon={<CloudUpload />}
            >
              Upload Now
            </GradientButton>
          </Grid>
        </Grid>
      </ModernPaper>

      {/* Upload Result Modal */}
      <Dialog open={uploadModalOpen} onClose={() => setUploadModalOpen(false)}>
        <DialogTitle>
          {uploadResult.success ? "🎉 Success!" : "⚠️ Error"}
        </DialogTitle>
        <DialogContent>
          <Typography>{uploadResult.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadModalOpen(false)}>Dismiss</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to delete “{deleteTarget?.name}”?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>Yes</Button>
        </DialogActions>
      </Dialog>

      {/* Table */}
      <ModernPaper>
        <MaterialReactTable
          columns={columns}
          data={mediaList}
          enableSorting
          enablePagination
          initialState={{ density: 'comfortable', pagination: { pageSize: 10 } }}
          muiTablePaperProps={{ sx: { borderRadius: 3, border: '1px solid #f1f5f9' } }}
        />
      </ModernPaper>
    </Container>
  );
}
